import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DEFAULT_MAP_CENTER, SYMBOL_LIST, USStates, metadata, relativePaths } from '@app/constants';
import { SlideInOutAnimation } from '@app/shared/animations/expand-collapse.antiomation';
import _ from 'lodash';
import { Map, LngLat, LngLatBounds, GeoJSONSource } from 'mapbox-gl';
import { DialogSearchWaterways } from './dialog-search-waterways/dialog-search-waterways';
import config from '@config/index';
import { ActivatedRoute, Router } from '@angular/router';
import { waterwaysActions, waterwaysSelector, waterwaysState } from '@app/core/store/waterways/waterways.slice';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { ResponseApi, createMapUrl } from '@app/utils';
import { fishingMapAction, fishingMapSelector } from '@app/core/store/fishing-map/fishing-map.slice';
import { FilterWaterwaysPayload, GeocodingRegionPayload } from '@app/core/features/waterways/waterway.payload';
import { CatchMapPayload, SpotMapPayload } from '@app/core/features/fishing-map/fishing-map.payload';
import { Meta, Title } from '@angular/platform-browser';

@Component({
  selector: 'app-landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
  animations: [SlideInOutAnimation],
})
export class LandingPageComponent implements OnInit, OnDestroy {
  center: LngLat;
  searchText = '';
  animationState = 'in';
  style = `${config.mapboxStylePrefix}${config.mapboxStyleUrl}`;
  defaultCenter = DEFAULT_MAP_CENTER;
  createMapUrl = createMapUrl;
  map: Map;
  isLoading: boolean;
  region: any;
  isFishingMap = false;
  isStategMap = false;
  isWaterwayMap = false;
  waterways: any = [];
  waterwayRegion$: any;
  waterwayRegion: any;
  waterwayLanding$: any;
  waterwayLanding: any;
  userFeatured$: any;
  path = window.location.pathname;

  waterwaysRegionServicesubs: Subscription;
  waterwayRegionSubs: Subscription;
  waterwayLandingSubs: Subscription;
  getCatcPublichMapSubs: Subscription;
  getSpotPublicMapSubs: Subscription;
  geocodingRegionSubs: Subscription;

  constructor(
    private store: Store<{}>,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private titleService: Title,
    private metaService: Meta,
  ) {}

  ngOnInit() {
    this.userFeatured$ = this.store.select(waterwaysSelector.userFeatured);
    this.geocodingRegionSubs = this.store.select(waterwaysSelector.geocodingRegion).subscribe((response: any) => {
      const gepRegion = response.result;
      if (gepRegion) {
        if (!gepRegion.features[0]) {
          this.router.navigate([relativePaths.PAGE_NOT_FOUND]);
        }
        this.region = gepRegion.features[0];
        this.setMeta(this.region.text);
      }
    });

    this.getCatcPublichMapSubs = this.store.select(fishingMapSelector.getCatcPublichMap).subscribe((response: ResponseApi<any>) => {
      this.setLayerOfUserData('CATCH', response.result);
    });

    this.getSpotPublicMapSubs = this.store.select(fishingMapSelector.getSpotPublicMap).subscribe((response: ResponseApi<any>) => {
      this.setLayerOfUserData('SPOT', response.result);
    });

    this.route.params.subscribe(({ country, state, region }) => {
      window.scrollTo(0, 0);
      this.path = createMapUrl({ country, state, region });
      this.isWaterwayMap = !!region;
      this.isStategMap = !region && !!state;
      this.isFishingMap = !region && !state;

      if (this.isWaterwayMap) {
        const params = { isoCountryCode: country, stateAbbr: state, vanityName: region };
        this.store.dispatch(waterwaysActions.getWaterwayLanding(params));
        this.waterwayLanding$ = this.store.select(waterwaysSelector.getWaterwayLanding.collection(this.path));
        this.getWaterwayLandingSelector();
      }

      if (this.isStategMap) {
        this.fetchGeocodingRegion(state);
        this.store.dispatch(waterwaysActions.getWaterwayRegion({ country, stateName: state }));
        this.waterwayRegion$ = this.store.select(waterwaysSelector.getWaterwayRegion.collection(this.path));
        this.getWaterwayRegionSelector();
      }

      if (this.isFishingMap) {
        this.store.dispatch(waterwaysActions.userFeatured());
      }
    });
  }

  ngOnDestroy(): void {
    if (this.waterwaysRegionServicesubs) {
      this.waterwaysRegionServicesubs.unsubscribe();
    }
    if (this.waterwayRegionSubs) {
      this.waterwayRegionSubs.unsubscribe();
    }
    if (this.waterwayLandingSubs) {
      this.waterwayLandingSubs.unsubscribe();
    }
    this.geocodingRegionSubs.unsubscribe();
    this.getCatcPublichMapSubs.unsubscribe();
    this.getSpotPublicMapSubs.unsubscribe();
    this.store.dispatch(waterwaysActions.cleanState(waterwaysState.geocodingRegion));
  }

  mapLoad(evt: any) {
    this.map = evt;
    this.fitBoundsMap();
    this.flyToMap();
  }

  fitBoundsMap() {
    if (this.map && this.region && this.waterwayRegion) {
      this.map.fitBounds(this.region.bbox);
    }
  }

  flyToMap() {
    const waterway: any = _.get(this, 'waterwayLanding.waterway', {});
    if (this.map && waterway.longitude && waterway.latitude) {
      const center: any = [waterway.longitude, waterway.latitude];
      this.map.flyTo({ center, zoom: 12, duration: 1000, essential: true });
      setTimeout(() => {
        this.fetchCatchPublicMap(this.map.getBounds());
        this.fetchSpotPublicMap(this.map.getBounds());
        Object.keys(SYMBOL_LIST).forEach(symbol => {
          if (SYMBOL_LIST[symbol].icon) {
            this.map.loadImage(SYMBOL_LIST[symbol].icon, (error: any, image: any) => {
              if (error) {
                throw error;
              }
              if (!this.map.hasImage(SYMBOL_LIST[symbol].image)) {
                this.map.addImage(SYMBOL_LIST[symbol].image, image);
              }
            });
          }
        });
      }, 1200);
    }
  }

  centerChange = (center: LngLat) => {
    this.center = center;
  };

  searchWaterways = query => {
    if (!_.isEmpty(query)) {
      this.store.dispatch(waterwaysActions.filterWaterways(new FilterWaterwaysPayload(query)));
    }
    this.dialog.open(DialogSearchWaterways);
  };

  getWaterwayLandingSelector = () => {
    if (this.waterwayLandingSubs) {
      this.waterwayLandingSubs.unsubscribe();
    }

    this.waterwayLandingSubs = this.store.select(waterwaysSelector.getWaterwayLanding.collection(this.path)).subscribe((response: any) => {
      if (response) {
        if (response.error) {
          this.router.navigate([relativePaths.PAGE_NOT_FOUND]);
        }
        this.waterwayLanding = response.result;
        if (response.result && response.result.waterway) {
          const { name, description, state } = this.waterwayLanding.waterway;
          const stateInfo = USStates.find(s => s.name === state);
          this.setMeta(`${name} ${stateInfo.acronym}`, description);
        }
        this.flyToMap();
      }
    });
  };

  getWaterwayRegionSelector() {
    if (this.waterwayRegionSubs) {
      this.waterwayRegionSubs.unsubscribe();
    }
    this.waterwayRegionSubs = this.store.select(waterwaysSelector.getWaterwayRegion.collection(this.path)).subscribe((response: any) => {
      if (response) {
        if (response.error) {
          this.router.navigate([relativePaths.PAGE_NOT_FOUND]);
        }
        this.waterwayRegion = response.result;
        this.fitBoundsMap();
      }
    });
  }

  setMeta(name: string, description: string = null) {
    const metaData = metadata.MAP;
    _.templateSettings.interpolate = /{{([\s\S]+?)}}/g;
    const compiledTitle = _.template(metaData.title);
    const compiledDescription = _.template(description || metaData.description);
    this.titleService.setTitle(compiledTitle({ name }));
    this.metaService.updateTag({ name: 'title', content: compiledTitle({ name }) });
    this.metaService.updateTag({ name: 'description', content: compiledDescription({ name }) });
  }

  interactiveMap(divName: string) {
    if (divName === 'overlay-backdrop') {
      this.animationState = this.animationState === 'out' ? 'in' : 'out';
    }
  }

  navigateWaterway(waterway: any) {
    this.router.navigate([createMapUrl(waterway)]);
  }

  navigateSignup() {
    this.router.navigate([relativePaths.SIGN_UP]);
  }

  truncate(str) {
    return _.truncate(str, { length: 500, separator: ' ' });
  }

  setLayerOfUserData(type: string, points) {
    if (points == null || !this.map) {
      return;
    }
    const layerId = SYMBOL_LIST[type].id;
    const icon = SYMBOL_LIST[type].image;
    const source = this.map.getSource(layerId);
    const feature: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: points.map(point => ({
        type: 'Feature',
        properties: { photo: point.photo, lat: point.latitude, lng: point.longitude, icon },
        geometry: {
          type: 'Point',
          coordinates: [point.longitude, point.latitude],
        },
      })),
    };

    if (source) {
      (source as GeoJSONSource).setData(feature);
    } else {
      this.map.addSource(layerId, { type: 'geojson', data: feature });
      this.map.addLayer({
        id: layerId,
        type: 'symbol',
        source: layerId,
        layout: {
          visibility: 'visible',
          'icon-image': '{icon}',
          'icon-size': 0.2,
          'icon-allow-overlap': true,
        },
      });
    }
  }

  private fetchCatchPublicMap(lnglatBound: LngLatBounds): void {
    const ne = lnglatBound.getNorthEast();
    const sw = lnglatBound.getSouthWest();
    const payload = new CatchMapPayload(ne.lat, ne.lng, sw.lat, sw.lng, null, null, null);
    this.store.dispatch(fishingMapAction.getCatchPublicMap(payload));
  }

  private fetchSpotPublicMap(lnglatBound: LngLatBounds): void {
    const ne = lnglatBound.getNorthEast();
    const sw = lnglatBound.getSouthWest();
    const payload = new SpotMapPayload(ne.lat, ne.lng, sw.lat, sw.lng);
    this.store.dispatch(fishingMapAction.getSpotPublicMap(payload));
  }

  private fetchGeocodingRegion(state) {
    if (this.waterwaysRegionServicesubs) {
      this.waterwaysRegionServicesubs.unsubscribe();
    }
    const payload = new GeocodingRegionPayload(state);
    this.store.dispatch(waterwaysActions.geocodingRegion(payload));
  }
}
